// Pure data-shaping helpers for the Alt Surface Toolkit's daily-close input.
//
// Scope (IM-2486): assemble the daily-close price frame the eventual
// generated-surface methodology will consume. NO methodology maths live here
// (no stdev / EWMA / ratio / ATM-scaling) — that is the following story. This
// file only turns raw hourly perp candles into a clean, UTC-aligned, wide
// daily-close frame, and resolves the candlestick request strings.
//
// Kept asset-free and transport-free (no React, no WebSocket) so it is a clean,
// fast unit-test seam — mirrors the registry-rows.ts pattern.
import { CandleRequest } from '../model/candleData';
import { IntervalEnum } from '../../model/interval.enum';
import { isDeribitExchange } from '../../model/token';

// A single hourly observation, already parsed off the wire into a UTC epoch
// (ms) and a numeric close. The hook does the wire parsing; this module stays
// transport-agnostic.
export interface HourlyPoint {
  timeMs: number;
  close: number;
}

// One row of the wide daily-close frame, keyed on the UTC calendar day.
// `date` is the ISO UTC day (YYYY-MM-DD); `values` carries one close per
// column (ETH / BTC / token).
export interface DailyCloseRow {
  date: string;
  values: Record<string, number>;
}

// Fixed column keys for the three perps the toolkit always loads.
export const DAILY_CLOSE_COLUMNS = {
  ETH: 'ETH',
  BTC: 'BTC',
  TOKEN: 'TOKEN',
} as const;

export type DailyCloseColumn = (typeof DAILY_CLOSE_COLUMNS)[keyof typeof DAILY_CLOSE_COLUMNS];

// Hourly history window the toolkit requests, in days. 365d hourly per perp.
export const DAILY_CLOSE_HISTORY_DAYS = 365;

// Milliseconds in an hour / day. Used to floor the candle request window to the
// UTC hour (the hook's fetchCandles) so the server's hourly bars are
// hour-aligned, and to size the history window in whole days.
export const HOUR_MS = 3_600_000;
export const DAY_MS = 24 * HOUR_MS;

// Hourly candle interval ('01:00:00'). The backend GetTimeSeries equivalent is
// frequency=60 (minutes); the candlestick method takes the duration string.
export const HOURLY_INTERVAL = IntervalEnum.ONE_HOUR;

// The ISO UTC calendar day (YYYY-MM-DD) for an epoch-ms instant. Day boundaries
// are UTC by construction (slice of toISOString), never local — the toolkit's
// resample boundary is UTC.
export const toUtcDay = (timeMs: number): string => new Date(timeMs).toISOString().slice(0, 10);

// Collapses an hourly series to one close per UTC day: the LAST (latest
// timestamp) close within each day. NaN / non-finite closes are dropped before
// the reduction (the `dropna` step). Input order is irrelevant — selection is
// by timestamp, not array position.
export const toDailyLastClose = (points: HourlyPoint[]): Map<string, number> => {
  const latestMsByDay = new Map<string, number>();
  const closeByDay = new Map<string, number>();

  for (const point of points) {
    if (!Number.isFinite(point.timeMs) || !Number.isFinite(point.close)) continue;
    const day = toUtcDay(point.timeMs);
    const seenMs = latestMsByDay.get(day);
    if (seenMs === undefined || point.timeMs >= seenMs) {
      latestMsByDay.set(day, point.timeMs);
      closeByDay.set(day, point.close);
    }
  }

  return closeByDay;
};

// The top-of-hour UTC key (YYYY-MM-DDTHH:00:00.000Z) for an epoch-ms instant —
// the hourly analogue of toUtcDay. The bar is bucketed by the UTC hour it falls
// in, NOT by its raw sub-hour stamp: venues label hourly bars on different
// sub-hour instants (open- vs close-time, a seconds/precision difference), so a
// raw-instant key would not inner-join across venues even though every venue has
// exactly one bar per hour. Flooring to the hour is the join granularity the
// three-way intersection is actually on — mirroring how toUtcDay floors to the
// day — and is NOT a resample: each venue still contributes its one hourly close.
export const toUtcHour = (timeMs: number): string =>
  `${new Date(timeMs).toISOString().slice(0, 13)}:00:00.000Z`;

// Collapses an hourly series to one close per UTC hour: the LAST (latest
// timestamp) close within each hour, keyed by the top-of-hour UTC instant. The
// hourly analogue of toDailyLastClose with NO daily resample. NaN / non-finite
// closes are dropped (the `dropna` step); within an hour the latest timestamp
// wins (the >= tie-break), matching toDailyLastClose. Input order is irrelevant.
export const toHourlyLastClose = (points: HourlyPoint[]): Map<string, number> => {
  const latestMsByHour = new Map<string, number>();
  const closeByHour = new Map<string, number>();

  for (const point of points) {
    if (!Number.isFinite(point.timeMs) || !Number.isFinite(point.close)) continue;
    const hour = toUtcHour(point.timeMs);
    const seenMs = latestMsByHour.get(hour);
    if (seenMs === undefined || point.timeMs >= seenMs) {
      latestMsByHour.set(hour, point.timeMs);
      closeByHour.set(hour, point.close);
    }
  }

  return closeByHour;
};

// Shared inner-join core for the wide frames. Each column's series is reduced to
// a key->close map by `reduce`, then the columns are inner-joined on the key: a
// row survives only when EVERY column has a close for that key (the wide-frame
// `dropna`). Rows are returned in ascending key order. The key is a UTC day
// (daily) or a full ISO hour instant (hourly) — both sort lexically.
//
// Inner-join is deliberate: the downstream ratio maths needs every row fully
// populated across ETH / BTC / token, so a key missing any leg is not a partial
// row to carry — it is dropped.
const assembleWideFrame = (
  seriesByColumn: Record<string, HourlyPoint[]>,
  reduce: (points: HourlyPoint[]) => Map<string, number>,
): DailyCloseRow[] => {
  const columns = Object.keys(seriesByColumn);
  if (columns.length === 0) return [];

  const reducedByColumn: Record<string, Map<string, number>> = {};
  for (const column of columns) {
    reducedByColumn[column] = reduce(seriesByColumn[column]);
  }

  // Candidate keys = intersection across all columns. Seed from the first
  // column, then keep only keys present in every other column.
  const [firstColumn, ...restColumns] = columns;
  let candidateKeys = [...reducedByColumn[firstColumn].keys()];
  for (const column of restColumns) {
    const keyMap = reducedByColumn[column];
    candidateKeys = candidateKeys.filter((key) => keyMap.has(key));
  }

  candidateKeys.sort();

  return candidateKeys.map((date) => {
    const values: Record<string, number> = {};
    for (const column of columns) {
      values[column] = reducedByColumn[column].get(date) as number;
    }
    return { date, values };
  });
};

// Assembles the wide DAILY-close frame from per-column hourly series: each column
// reduced to its UTC daily-last close, then inner-joined on the UTC day. This is
// the generator's basis.
export const assembleDailyCloseFrame = (
  seriesByColumn: Record<string, HourlyPoint[]>,
): DailyCloseRow[] => assembleWideFrame(seriesByColumn, toDailyLastClose);

// Assembles the wide HOURLY frame from the SAME per-column hourly series, with no
// daily resample: each column keyed by its top-of-hour UTC instant, inner-joined
// on that hour. `date` carries the ISO hour timestamp here (not a calendar day);
// downstream compute reads only `values`, so the key semantics are transparent to
// it. The exploratory hourly-return hvol path consumes this frame.
export const assembleHourlyCloseFrame = (
  seriesByColumn: Record<string, HourlyPoint[]>,
): DailyCloseRow[] => assembleWideFrame(seriesByColumn, toHourlyLastClose);

// The latest (most recent UTC day) token close in an assembled frame, or null
// when the frame is empty. Used as the token-spot proxy — see the hook's
// FLAGGED note on spot sourcing.
export const latestTokenClose = (
  frame: DailyCloseRow[],
  column: string = DAILY_CLOSE_COLUMNS.TOKEN,
): number | null => {
  if (frame.length === 0) return null;
  const last = frame[frame.length - 1];
  const value = last.values[column];
  return Number.isFinite(value) ? value : null;
};

// ---------------------------------------------------------------------------
// Candlestick request resolution
// ---------------------------------------------------------------------------

export interface PerpCandleTarget {
  source: string;
  underlying: string;
  label: string;
}

// ETH/BTC are literal, confirmed against timeseries-provider.tsx: the perp
// candlestick keys on underlying=<base ccy>, label='PERPETUAL', source='DERIBIT'.
export const ETH_PERP_TARGET: PerpCandleTarget = {
  source: 'DERIBIT',
  underlying: 'ETH',
  label: 'PERPETUAL',
};

export const BTC_PERP_TARGET: PerpCandleTarget = {
  source: 'DERIBIT',
  underlying: 'BTC',
  label: 'PERPETUAL',
};

// Resolves the candlestick target for the selected-token perp. The `underlying`
// follows the same convention as getTokenCandlestickSymbol (src/model/token.ts):
// deribit keys on `base_quote` (ETH -> 'ETH', BTC -> 'BTC'), every generated
// venue keys on `exchange_symbol`. Wire format for the generated venues verified
// against TEST 2026-10-04 — the symbol is used VERBATIM (base_quote and base_ccy
// both return -32603 Internal error there); the venue encodes it differently —
// binance drops the separator ('BNBUSDT', 'MORPHOUSDT'), mexc keeps it
// ('HYPE_USDT') — so we never transform it, we pass the field through. The
// deribit branch matters because a deribit token's exchange_symbol is
// '<CCY>-PERPETUAL' (e.g. 'ETH-PERPETUAL'), a different candlestick series from
// the 'ETH' anchor; routing deribit through base_quote lets ETH self-replicate.
// `source` is the venue upper-cased ('BINANCE' / 'MEXC' / 'DERIBIT'), falling
// back to DERIBIT when absent.
export const resolveTokenPerpTarget = (token: {
  source: string;
  exchangeSymbol: string;
  baseQuote: string;
}): PerpCandleTarget => {
  const source = (token.source || '').toUpperCase() || 'DERIBIT';
  return {
    source,
    underlying: isDeribitExchange(source) ? token.baseQuote : token.exchangeSymbol,
    label: 'PERPETUAL',
  };
};

// Builds a 365d hourly candlestick request for a perp target. `id` is the
// caller's channel id; begin/end are the ISO UTC window bounds.
export const buildPerpCandleRequest = (
  id: string,
  target: PerpCandleTarget,
  beginIso: string,
  endIso: string,
): CandleRequest =>
  new CandleRequest(
    id,
    beginIso,
    endIso,
    HOURLY_INTERVAL,
    target.source,
    target.underlying,
    target.label,
  );
